import { Invoice } from '@wonderschool/common-base-types';
import { showToast, ToastTypeEnum } from '@wonderschool/common-base-ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { setOrganizationInvoicePlan, updateOrganizationInvoice } from '../../../../api/firebase/invoices';
import { useOrganization } from '../../../../hooks/useOrganizations';
import { InvoiceTypeData } from '../../../../redux/reducers/billing/invoices';
import { logError } from '../../../../rollbar';
import { useStudent } from '../../../../students/studentsHooks';
import './InvoiceDetail.scss';
import { InvoiceDetailDetails } from './InvoiceDetailDetails';
import { InvoiceDetailContainerPayments } from './InvoiceDetailPayments';
import { InvoiceDetailProcessingFeesPaidByFamilyDisclaimer } from './InvoiceDetailProcessingFeesPaidByFamilyDisclaimer';
import { InvoiceDetailSummary } from './InvoiceDetailSummary';

export type InvoiceDetailContainerProps = {
  invoices: InvoiceTypeData;
  invoiceType: string;
  initallySelectedInvoice?: Invoice | null;
};

export type InvoiceUpdateOptions = { suppressErrorToast?: boolean };

export function InvoiceDetailContainer({
  invoices,
  invoiceType,
  initallySelectedInvoice,
}: InvoiceDetailContainerProps) {
  const currentOrganization = useOrganization();
  const { t } = useTranslation();
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice>(
    initallySelectedInvoice ?? invoices[invoiceType]?.selected
  );
  const student = useStudent(selectedInvoice.student && selectedInvoice.student.id);

  const organizationId = currentOrganization.id;

  useEffect(() => {
    if (!selectedInvoice) setSelectedInvoice(invoices[invoiceType]?.selected);
  }, [invoiceType, invoices, selectedInvoice]);

  const handleInvoiceUpdate = async (updateData: Partial<Invoice>, options: InvoiceUpdateOptions = {}) => {
    if (!selectedInvoice) return;

    const dataToUpdate = {
      ...updateData,
      id: updateData.id || selectedInvoice.id,
    };

    try {
      if (invoiceType === 'recurring') {
        dataToUpdate.dateDue = Date.now();
        await setOrganizationInvoicePlan(organizationId, dataToUpdate);
      } else {
        await updateOrganizationInvoice(organizationId, dataToUpdate);
      }
    } catch (error) {
      logError('InvoiceDetailContainer: failed saving invoice', error);
      if (!options.suppressErrorToast) {
        showToast(
          ToastTypeEnum.Error,
          t(invoiceType === 'recurring' ? 'billing.invoicePlan.saveFailed' : 'billing.invoice.saveFailed')
        );
      }
      throw error;
    }

    const updatedInvoice = { ...selectedInvoice, ...dataToUpdate };
    setSelectedInvoice(updatedInvoice);
  };

  return (
    <div className="mx-auto grid max-w-screen-md grid-cols-1 gap-y-4">
      <InvoiceDetailSummary
        organization={currentOrganization}
        invoice={selectedInvoice}
        student={student}
        invoiceType={invoiceType}
        onInvoiceUpdate={handleInvoiceUpdate}
      />
      <InvoiceDetailDetails selectedInvoice={selectedInvoice} student={student} />
      <InvoiceDetailContainerPayments selectedInvoice={selectedInvoice} />
      <InvoiceDetailProcessingFeesPaidByFamilyDisclaimer
        organization={currentOrganization}
        selectedInvoice={selectedInvoice}
      />
    </div>
  );
}
